import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import { multiplyPosition } from "@brickme/project-core/src/model/geom.ts";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import { isEqual } from "@brickme/project-core/src/utils/unknown.ts";

type PicturePatch = Partial<
	Omit<PictureConfiguration, "updatedAt" | "numberOfBricks">
>;

function applyPicturePatch(
	sourceImageDimensions: GenericSize<number>,
	previous: PictureConfiguration,
	patch: PicturePatch,
): PictureConfiguration {
	const intendedPicture = {
		...previous,
		...patch,
	};
	const intendedPictureWithBricks = {
		...intendedPicture,
		numberOfBricks: {
			width:
				intendedPicture.basePlateSize *
				intendedPicture.numberOfBasePlates.width,
			height:
				intendedPicture.basePlateSize *
				intendedPicture.numberOfBasePlates.height,
		},
	};

	// The offset is measured in bricks, so resizing the baseplate has to rescale it
	// or the crop lands on a different part of the photo (and the constrain below
	// then silently drags it to the nearest legal spot). An offset supplied by the
	// same patch is already in the new scale, so it is left alone.
	const proposedImageZoomOffset =
		patch.basePlateSize === undefined ||
		patch.basePlateSize === previous.basePlateSize ||
		patch.imageZoomOffset !== undefined
			? intendedPictureWithBricks.imageZoomOffset
			: multiplyPosition(
					intendedPictureWithBricks.imageZoomOffset,
					patch.basePlateSize / previous.basePlateSize,
				);

	// Make sure domain bounds are enforced
	const finalPicture = {
		...intendedPictureWithBricks,
		// If changing numberOfBasePlates or baseplateSize then this can become invalid
		imageZoomOffset: roundAndConstrainImageZoomOffset(
			sourceImageDimensions,
			intendedPictureWithBricks,
			proposedImageZoomOffset,
		),
	};

	// No change, use previous
	if (isEqual(finalPicture, previous)) {
		return previous;
	}

	// If imageZoom, basePlateSize or numberOfBasePlates are changed, then clear pens.
	// No sane way to scale them.
	if (
		finalPicture.imageZoom !== previous.imageZoom ||
		finalPicture.basePlateSize !== previous.basePlateSize ||
		!isEqual(finalPicture.numberOfBasePlates, previous.numberOfBasePlates)
	) {
		finalPicture.pen = [];
	}

	return finalPicture;
}

export default applyPicturePatch;
export type { PicturePatch };
